import React from "react";
import { ResponsiveContainer, PieChart, Pie, Cell, LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip } from "recharts";
import {
  translate,
  getNameIncidentsTypes,
  getNameIncidentsOrigins,
  getNameIncidentsStatus,
} from "../../../../utils/Common";
import { formatRange } from "../../Insights/CounterInsights/Incidents/incidentPeriodPresets";
import { formatDbDateTime, formatDuration } from "./reportPeriods";

const t = (key) => translate(`dashboard.incidents.report.${key}`);

// Reemplaza {0}, {1}… en una traducción
export const fill = (text, ...values) => values.reduce((acc, value, index) => acc.replace(`{${index}}`, value), text || "");

export const percentChange = (current, previous) => (previous ? Math.round(((current - previous) / previous) * 100) : null);

// Pastilla de cambio: el color dice si el cambio es bueno, no si subió o bajó
export const ChangePill = ({ current, previous, lowerIsGood = false }) => {
  const change = percentChange(current, previous);
  if (change === null) return <span className="text-[11px] text-muted-foreground">—</span>;
  if (change === 0) return <span className="rounded-full bg-muted px-2 py-0.5 text-[11px] font-semibold text-muted-foreground">= 0%</span>;
  const good = lowerIsGood ? change < 0 : change > 0;
  return (
    <span className={`whitespace-nowrap rounded-full px-2 py-0.5 text-[11px] font-semibold ${good ? "bg-success/12 text-success" : "bg-destructive/12 text-destructive"}`}>
      {change > 0 ? "▲" : "▼"} {Math.abs(change)}%
    </span>
  );
};

export const SectionTitle = ({ title, aside }) => (
  <div className="mb-3 flex flex-wrap items-baseline justify-between gap-2">
    <h3 className="m-0 text-base font-semibold tracking-tight text-foreground">{title}</h3>
    {aside && <span className="text-xs text-muted-foreground">{aside}</span>}
  </div>
);

export const Card = ({ children, className = "" }) => (
  <div className={`rounded-2xl border border-border bg-card p-4 ${className}`}>{children}</div>
);

// Frases del resumen, armadas con los mismos números que el resto del reporte (pantalla y PDF)
export const buildHighlights = (report) => {
  const { summary, previous, over48hCount, pendingFollowUpCount, topType } = report;
  const change = percentChange(summary.created, previous.created);
  const lines = [];

  lines.push(
    change === null
      ? fill(t("highlights.created"), summary.created)
      : fill(t(change <= 0 ? "highlights.createdLess" : "highlights.createdMore"), summary.created, Math.abs(change), previous.created)
  );
  lines.push(fill(t(summary.closed >= summary.created ? "highlights.closedAhead" : "highlights.closedBehind"), summary.closed, summary.open));
  if (summary.resolutionRate != null) {
    lines.push(
      fill(
        t("highlights.rate"),
        `${summary.resolutionRate}%`,
        summary.avgResolutionHours == null ? "—" : `${summary.avgResolutionHours} h`
      )
    );
  }
  if (topType) {
    const share = summary.created ? Math.round((topType.total / summary.created) * 1000) / 10 : 0;
    lines.push(fill(t("highlights.topType"), getNameIncidentsTypes(topType.id) || topType.name, topType.total, `${share}%`));
  }
  lines.push(fill(t("highlights.attention"), over48hCount, pendingFollowUpCount));
  return lines;
};

export const ReportHighlights = ({ report }) => {
  const lines = buildHighlights(report);

  return (
    <div className="rounded-2xl border border-border bg-gradient-to-br from-secondary/10 via-primary/5 to-primary/10 p-4">
      <h3 className="m-0 mb-2 text-sm font-semibold text-foreground">{t("highlights.title")}</h3>
      <ul className="m-0 flex list-disc flex-col gap-1 pl-5 text-sm leading-relaxed text-foreground">
        {lines.map((line) => (
          <li key={line}>{line}</li>
        ))}
      </ul>
    </div>
  );
};

const AGING_BUCKETS = [
  { key: "from0to24", label: "0 – 24 h", color: "var(--color-success)" },
  { key: "from24to48", label: "24 – 48 h", color: "var(--color-primary)" },
  { key: "from48to72", label: "48 – 72 h", color: "var(--color-warning)" },
  { key: "over72", label: "+72 h", color: "var(--color-error)" },
];

export const ReportFlow = ({ flow, aging, over48hCount }) => {
  const steps = [
    { label: t("flow.openAtStart"), value: flow.openAtStart, className: "bg-muted/60" },
    { sign: "+" },
    { label: "New Cases", value: flow.newCases, className: "bg-primary/10 text-primary-strong" },
    { sign: "−" },
    { label: "Resolved Cases", value: flow.resolvedCases, className: "bg-success/10 text-success" },
    { sign: "=" },
    { label: t("flow.openAtEnd"), value: flow.openAtEnd, className: "border border-border bg-card", extra: fill(t("flow.split"), flow.newOpen, flow.backlog) },
  ];
  const totalOpen = AGING_BUCKETS.reduce((sum, bucket) => sum + (aging[bucket.key] || 0), 0);

  return (
    <div className="flex flex-col gap-3">
      <div className="grid grid-cols-2 gap-2 sm:flex sm:items-stretch">
        {steps.map((step, index) =>
          step.sign ? (
            <div key={index} className="hidden items-center text-xl font-light text-muted-foreground sm:flex">
              {step.sign}
            </div>
          ) : (
            <div key={index} className={`min-w-0 flex-1 rounded-2xl p-3 ${step.className} ${index === steps.length - 1 ? "col-span-2" : ""}`}>
              <p className="m-0 text-[11px] font-semibold">{step.label}</p>
              <p className="m-0 mt-1 text-2xl font-semibold leading-none text-foreground">{step.value}</p>
              {step.extra && <p className="m-0 mt-1 text-[11px] text-muted-foreground">{step.extra}</p>}
            </div>
          )
        )}
      </div>

      <Card>
        <div className="flex flex-wrap items-baseline justify-between gap-2">
          <p className="m-0 text-sm font-semibold text-foreground">{fill(t("flow.agingTitle"), totalOpen)}</p>
          <p className="m-0 text-xs font-semibold text-destructive">{fill(t("flow.over48"), over48hCount)}</p>
        </div>
        {totalOpen > 0 ? (
          <div className="mt-3 flex h-8 gap-0.5 overflow-hidden rounded-lg">
            {AGING_BUCKETS.filter((bucket) => aging[bucket.key] > 0).map((bucket) => (
              <div
                key={bucket.key}
                className="flex min-w-[28px] items-center px-2 text-xs font-semibold text-white"
                style={{ flex: aging[bucket.key], background: bucket.color }}
              >
                {aging[bucket.key]}
              </div>
            ))}
          </div>
        ) : (
          <p className="m-0 mt-2 text-xs text-muted-foreground">{t("flow.noOpen")}</p>
        )}
        <div className="mt-2 flex flex-wrap gap-x-4 gap-y-1 text-[11px] text-muted-foreground">
          {AGING_BUCKETS.map((bucket) => (
            <span key={bucket.key} className="inline-flex items-center gap-1.5">
              <i className="inline-block h-2 w-2 rounded-sm" style={{ background: bucket.color }} />
              {bucket.label} · {aging[bucket.key] || 0}
            </span>
          ))}
        </div>
      </Card>
    </div>
  );
};

// Tabla por tipo u origen: cantidad, % del período, período anterior y cambio
export const ReportCatalogTable = ({ rows, total, nameOf, emptyLabel }) => {
  if (!rows.length) return <p className="m-0 text-sm text-muted-foreground">{emptyLabel}</p>;
  const max = Math.max(...rows.map((row) => row.total), 1);

  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead>
          <tr className="text-left text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
            <th className="py-2 pr-3 font-semibold">{t("table.name")}</th>
            <th className="hidden w-1/3 py-2 pr-3 sm:table-cell" aria-hidden />
            <th className="py-2 pr-3 text-right font-semibold">{t("table.period")}</th>
            <th className="py-2 pr-3 text-right font-semibold">%</th>
            <th className="hidden py-2 pr-3 text-right font-semibold sm:table-cell">{t("table.previous")}</th>
            <th className="py-2 text-right font-semibold">{t("table.change")}</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((row, index) => (
            <tr key={row.id ?? "none"} className="border-t border-border">
              <td className={`py-1.5 pr-3 ${index === 0 && row.total ? "font-semibold" : ""}`}>{nameOf(row)}</td>
              <td className="hidden py-1.5 pr-3 sm:table-cell">
                <div className="h-1.5 w-full overflow-hidden rounded-full bg-muted">
                  <div className="h-full rounded-full bg-primary" style={{ width: `${(row.total / max) * 100}%` }} />
                </div>
              </td>
              <td className="py-1.5 pr-3 text-right font-semibold">{row.total}</td>
              <td className="py-1.5 pr-3 text-right text-muted-foreground">
                {total ? `${Math.round((row.total / total) * 1000) / 10}%` : "—"}
              </td>
              <td className="hidden py-1.5 pr-3 text-right text-muted-foreground sm:table-cell">{row.previous}</td>
              <td className="py-1.5 text-right">
                <ChangePill current={row.total} previous={row.previous} lowerIsGood />
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

export const typeName = (row) => (row.id ? getNameIncidentsTypes(row.id) || row.name : t("noType"));
export const originName = (row) => (row.id ? getNameIncidentsOrigins(row.id) || row.name : t("noOrigin"));

const ORIGIN_COLORS = ["var(--color-primary)", "var(--color-secondary)", "#9B4DD6", "#FF8A65", "#4C86F0", "#babeee", "#c7c7cc"];

export const ReportOrigins = ({ rows, total }) => {
  const withData = rows.filter((row) => row.total > 0);
  return (
    <div className="flex flex-col items-center gap-4 md:flex-row md:items-start">
      {withData.length > 0 && (
        <div className="h-[150px] w-[150px] shrink-0">
          <ResponsiveContainer width="100%" height="100%">
            <PieChart>
              <Pie data={withData} dataKey="total" nameKey="id" innerRadius={45} outerRadius={70} paddingAngle={1} isAnimationActive={false}>
                {withData.map((row, index) => (
                  <Cell key={row.id ?? "none"} fill={ORIGIN_COLORS[index % ORIGIN_COLORS.length]} />
                ))}
              </Pie>
            </PieChart>
          </ResponsiveContainer>
        </div>
      )}
      <div className="w-full min-w-0">
        <ReportCatalogTable rows={rows} total={total} nameOf={originName} emptyLabel={t("empty")} />
      </div>
    </div>
  );
};

const TrendChart = ({ data, dataKey, color, unit }) => (
  <div className="h-[150px] w-full">
    <ResponsiveContainer width="100%" height="100%">
      <LineChart data={data} margin={{ top: 16, right: 12, left: 0, bottom: 0 }}>
        <CartesianGrid strokeDasharray="3 3" stroke="var(--border-light)" vertical={false} />
        <XAxis dataKey="label" fontSize={10} stroke="var(--text-secondary)" interval={0} />
        <YAxis width={32} fontSize={10} stroke="var(--text-secondary)" />
        <Tooltip formatter={(value) => (value == null ? "—" : `${value}${unit}`)} />
        <Line type="monotone" dataKey={dataKey} stroke={color} strokeWidth={2.5} dot={{ r: 3 }} connectNulls isAnimationActive={false} />
      </LineChart>
    </ResponsiveContainer>
  </div>
);

export const ReportTrend = ({ trend, labelOf }) => {
  const data = trend.map((period) => ({ ...period, label: labelOf(period) }));
  const last = trend[trend.length - 1];
  const before = trend[trend.length - 2];
  const diff = (key) => (last?.[key] == null || before?.[key] == null ? null : Math.round((last[key] - before[key]) * 10) / 10);
  const rateDiff = diff("resolutionRate");
  const timeDiff = diff("avgResolutionHours");

  return (
    <div className="flex flex-col gap-3">
      <div className="grid gap-3 md:grid-cols-2">
        <Card>
          <div className="flex items-start justify-between gap-2">
            <p className="m-0 text-sm font-semibold">Resolution Rate</p>
            <p className="m-0 text-right text-sm">
              <b>{last?.resolutionRate == null ? "—" : `${last.resolutionRate}%`}</b>{" "}
              {rateDiff != null && (
                <span className={rateDiff >= 0 ? "text-success" : "text-destructive"}>
                  {rateDiff >= 0 ? "▲" : "▼"} {Math.abs(rateDiff)} pts
                </span>
              )}
            </p>
          </div>
          <TrendChart data={data} dataKey="resolutionRate" color="var(--color-primary)" unit="%" />
        </Card>
        <Card>
          <div className="flex items-start justify-between gap-2">
            <p className="m-0 text-sm font-semibold">Average Resolution Time</p>
            <p className="m-0 text-right text-sm">
              <b>{last?.avgResolutionHours == null ? "—" : `${last.avgResolutionHours} h`}</b>{" "}
              {timeDiff != null && (
                <span className={timeDiff <= 0 ? "text-success" : "text-destructive"}>
                  {timeDiff >= 0 ? "▲" : "▼"} {Math.abs(timeDiff)} h
                </span>
              )}
            </p>
          </div>
          <TrendChart data={data} dataKey="avgResolutionHours" color="var(--color-secondary)" unit=" h" />
        </Card>
      </div>

      <Card className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
              <th className="py-2 pr-3 font-semibold">{t("table.periodName")}</th>
              <th className="py-2 pr-3 text-right font-semibold">{translate("dashboard.insights.incidents.kpi.created")}</th>
              <th className="py-2 pr-3 text-right font-semibold">{translate("dashboard.insights.incidents.kpi.closed")}</th>
              <th className="hidden py-2 pr-3 text-right font-semibold sm:table-cell">{translate("dashboard.insights.incidents.kpi.open")}</th>
              <th className="py-2 pr-3 text-right font-semibold">RR</th>
              <th className="py-2 text-right font-semibold">{translate("dashboard.insights.incidents.kpi.avgTime")}</th>
            </tr>
          </thead>
          <tbody>
            {data.map((period, index) => (
              <tr key={period.dateFrom} className={`border-t border-border ${index === data.length - 1 ? "bg-primary/5 font-semibold" : ""}`}>
                <td className="whitespace-nowrap py-1.5 pr-3">
                  {period.label} <span className="hidden font-normal text-muted-foreground md:inline">· {formatRange(period.dateFrom, period.dateTo)}</span>
                </td>
                <td className="py-1.5 pr-3 text-right">{period.created}</td>
                <td className="py-1.5 pr-3 text-right">{period.closed}</td>
                <td className="hidden py-1.5 pr-3 text-right sm:table-cell">{period.open}</td>
                <td className="py-1.5 pr-3 text-right">{period.resolutionRate == null ? "—" : `${period.resolutionRate}%`}</td>
                <td className="py-1.5 text-right">{period.avgResolutionHours == null ? "—" : `${period.avgResolutionHours} h`}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </Card>
    </div>
  );
};

const STATUS_DOT = { 1: "#86868b", 2: "var(--color-warning)", 3: "var(--color-primary)", 7: "var(--color-secondary)" };

// Tabla de incidencias que requieren atención: más de 48 h abiertas o pendientes de seguimiento
export const ReportIncidentTable = ({ rows, variant, onOpen }) => {
  if (!rows.length) return <p className="m-0 text-sm text-muted-foreground">{t("empty")}</p>;

  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead>
          <tr className="text-left text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
            <th className="py-2 pr-3 font-semibold">{t("table.case")}</th>
            <th className="hidden py-2 pr-3 font-semibold md:table-cell">{t("table.created")}</th>
            <th className="py-2 pr-3 font-semibold">{t("table.type")}</th>
            <th className="hidden py-2 pr-3 font-semibold lg:table-cell">{t("table.locker")}</th>
            <th className="hidden py-2 pr-3 font-semibold sm:table-cell">{t("table.status")}</th>
            <th className="hidden py-2 pr-3 font-semibold md:table-cell">{t("table.assigned")}</th>
            <th className="py-2 text-right font-semibold">{variant === "aged" ? t("table.age") : t("table.lastUpdate")}</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => (
            <tr key={row.id} className="border-t border-border">
              <td className="py-1.5 pr-3">
                <button type="button" className="font-semibold text-primary-strong hover:underline" onClick={() => onOpen(row.id)}>
                  #{row.id}
                </button>
              </td>
              <td className="hidden whitespace-nowrap py-1.5 pr-3 text-muted-foreground md:table-cell">{formatDbDateTime(row.createdAt)}</td>
              <td className="py-1.5 pr-3">{row.typeId ? getNameIncidentsTypes(row.typeId) || row.typeName : t("noType")}</td>
              <td className="hidden py-1.5 pr-3 text-muted-foreground lg:table-cell">{row.lockerName || "—"}</td>
              <td className="hidden whitespace-nowrap py-1.5 pr-3 sm:table-cell">
                <i className="mr-1.5 inline-block h-1.5 w-1.5 rounded-full align-middle" style={{ background: STATUS_DOT[row.statusId] || "#86868b" }} />
                {getNameIncidentsStatus(row.statusId)}
              </td>
              <td className="hidden py-1.5 pr-3 md:table-cell">{row.assignedName || <span className="text-muted-foreground">{t("unassigned")}</span>}</td>
              <td className="whitespace-nowrap py-1.5 text-right">
                {variant === "aged" ? (
                  <b className={row.ageHours >= 72 ? "text-destructive" : "text-warning"}>{formatDuration(row.ageHours)}</b>
                ) : (
                  <span>
                    <span className="text-muted-foreground">{formatDbDateTime(row.lastUpdateAt)}</span>
                    <span className="block text-[11px] text-muted-foreground">
                      {row.followUpReason === "onHold" ? t("reason.onHold") : fill(t("reason.noUpdate"), formatDuration(row.hoursSinceUpdate))}
                    </span>
                  </span>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};
